import { fetchData, apiClient } from "./client";

export async function listPassagens(params = {}) {
  const res = await fetchData("/passagens", "GET", null, { params });
  return res?.data ?? res ?? { rows: [], total: 0 };
}

export async function getPassagemDetail(id) {
  const res = await fetchData(`/passagens/${id}`, "GET");
  return res?.data ?? res;
}

export async function getPassagensFiltersMeta() {
  const res = await fetchData("/passagens/filters/meta", "GET");
  return res?.data ?? res;
}

export async function getPassagensPipeline(params = {}) {
  const res = await fetchData("/passagens/pipeline", "GET", null, { params });
  return res?.data ?? res;
}

export async function exportPassagensCsv(params = {}) {
  const response = await apiClient.get("/passagens/export", {
    params,
    responseType: "blob",
  });
  return response.data;
}

export async function syncPassagensBackfill(body = {}) {
  const res = await fetchData("/passagens/sync-backfill", "POST", body);
  return res?.data ?? res;
}

/** Console master — visão global (KPIs por workspace) */
export async function getPlatformGlobalPassengerSales(params = {}) {
  const { data } = await apiClient.get("/billing/admin/passenger-transport/sales", {
    params,
    timeout: 120000,
  });
  return data?.data ?? null;
}

/** Console master — lista consolidada de vendas (passagens_vendas) */
export async function getPlatformGlobalPassengerVendas(params = {}) {
  const { data } = await apiClient.get("/billing/admin/passenger-transport/vendas", {
    params,
    timeout: 120000,
  });
  return data?.data ?? null;
}

/** Console master — paridade legado × índice (todos os workspaces ou dominioId) */
export async function getPlatformGlobalPassengerParityCheck(params = {}) {
  const { data } = await apiClient.get("/billing/admin/passenger-transport/parity-check", {
    params,
    timeout: 300000,
  });
  return data?.data ?? null;
}

/** Console master — backfill do índice passagens_vendas (todos ou dominioId) */
export async function postPlatformGlobalPassengerSyncBackfill(params = {}, body = {}) {
  const { data } = await apiClient.post("/billing/admin/passenger-transport/sync-backfill", body, {
    params,
    timeout: 300000,
  });
  return data?.data ?? null;
}

/** Recalcula taxas parceiro/contratual/sobretaxa nas vendas do período. */
export async function postPlatformPassengerFeesBackfill(body = {}) {
  const { from, to, dominioId, ...rest } = body;
  const { data } = await apiClient.post(
    "/billing/admin/passenger-transport/fees-backfill",
    { from, to, dominioId, ...rest },
    { timeout: 300000 }
  );
  return data?.data ?? null;
}

export async function getPlatformDominioPassengerVendas(dominioId, params = {}) {
  const id = encodeURIComponent(String(dominioId));
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/passenger-transport/vendas`, { params });
  return data?.data ?? null;
}

export async function getPlatformDominioPassengerReconciliation(dominioId, params = {}, body = {}) {
  const id = encodeURIComponent(String(dominioId));
  const hasBody = body && Object.keys(body).length > 0;
  if (hasBody) {
    const { data } = await apiClient.post(`/billing/admin/dominios/${id}/passenger-transport/reconciliation`, body, {
      params,
      timeout: 180000,
    });
    return data?.data ?? null;
  }
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/passenger-transport/reconciliation`, { params, timeout: 180000 });
  return data?.data ?? null;
}

/** Console master — auditoria Mercado Pago (XPay) × passagens_vendas */
export async function getPlatformGlobalPassengerMercadoPagoAudit(params = {}) {
  const { data } = await apiClient.get("/billing/admin/passenger-transport/mercado-pago-audit", {
    params,
    timeout: 300000,
  });
  return data?.data ?? null;
}

/** Página de pagamentos MP para auditoria em lotes */
export async function getPlatformPassengerMercadoPagoAuditMpPage(params = {}) {
  const { data } = await apiClient.get("/billing/admin/passenger-transport/mercado-pago-audit", {
    params: { phase: "mp_search", ...params },
    timeout: 120000,
  });
  return data?.data ?? null;
}

/** Lote de workspaces na auditoria MP global */
export async function postPlatformPassengerMercadoPagoAuditBatch(params = {}, body = {}) {
  const { data } = await apiClient.post(
    "/billing/admin/passenger-transport/mercado-pago-audit",
    { phase: "reconcile", ...body },
    { params, timeout: 120000 }
  );
  return data?.data ?? null;
}

/** Página MP para auditoria de um workspace */
export async function getPlatformDominioPassengerMercadoPagoAuditMpPage(dominioId, params = {}) {
  const id = encodeURIComponent(String(dominioId));
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/passenger-transport/reconciliation`, {
    params: { phase: "mp_search", ...params },
    timeout: 120000,
  });
  return data?.data ?? null;
}

/** Console master — sincroniza índice local com status do Mercado Pago */
export async function postPlatformGlobalPassengerMercadoPagoSync(params = {}, body = {}) {
  const { data } = await apiClient.post(
    "/billing/admin/passenger-transport/mercado-pago-audit",
    { apply: true, ...body },
    { params, timeout: 300000 }
  );
  return data?.data ?? null;
}

export async function postPlatformDominioPassengerMercadoPagoSync(dominioId, params = {}, body = {}) {
  const id = encodeURIComponent(String(dominioId));
  const { data } = await apiClient.post(
    `/billing/admin/dominios/${id}/passenger-transport/reconciliation`,
    { apply: true, ...body },
    { params, timeout: 300000 }
  );
  return data?.data ?? null;
}

/** Auditoria de relatório Luna/Praxio (PDF) ou Totalbus RJ (XLS) × passagens_vendas */
export async function postPlatformDominioPassengerPartnerReportAudit(dominioId, body = {}) {
  const id = encodeURIComponent(String(dominioId));
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/passenger-transport/partner-report-audit`, body, {
    timeout: 300000,
  });
  return data?.data ?? null;
}

export async function listPlatformPassengerTaxProfiles(dominioId) {
  const id = encodeURIComponent(String(dominioId));
  const { data } = await apiClient.get(`/billing/admin/dominios/${id}/passenger-transport/tax-profiles`);
  return data?.data?.rows ?? [];
}

export async function savePlatformPassengerTaxProfile(dominioId, payload) {
  const id = encodeURIComponent(String(dominioId));
  const { data } = await apiClient.post(`/billing/admin/dominios/${id}/passenger-transport/tax-profiles`, payload);
  return data?.data ?? null;
}

export async function deletePlatformPassengerTaxProfile(dominioId, profileId) {
  const id = encodeURIComponent(String(dominioId));
  const { data } = await apiClient.delete(`/billing/admin/dominios/${id}/passenger-transport/tax-profiles`, {
    data: { id: profileId },
  });
  return data?.data ?? null;
}

export async function listPlatformPassengerPartnerGlobalFees() {
  const { data } = await apiClient.get("/billing/admin/passenger-transport/partner-fee-rates");
  return data?.data?.rows ?? [];
}

export async function savePlatformPassengerPartnerGlobalFee(payload) {
  const { data } = await apiClient.post("/billing/admin/passenger-transport/partner-fee-rates", payload);
  return data?.data ?? null;
}

export async function deletePlatformPassengerPartnerGlobalFee(rateId) {
  const { data } = await apiClient.delete("/billing/admin/passenger-transport/partner-fee-rates", {
    data: { id: rateId },
  });
  return data?.data ?? null;
}

export async function exportPlatformDominioPassengerFinancialCsv(dominioId, params = {}) {
  const id = encodeURIComponent(String(dominioId));
  const response = await apiClient.get(`/billing/admin/dominios/${id}/passenger-transport/financial-export`, {
    params,
    responseType: "blob",
  });
  return response.data;
}

/** Relatório no formato da planilha Mercado Pago (detalhe ou resumo VENDAS). */
export async function exportPlatformDominioMercadoPagoReportCsv(dominioId, params = {}) {
  const id = encodeURIComponent(String(dominioId));
  const response = await apiClient.get(`/billing/admin/dominios/${id}/passenger-transport/mercado-pago-report`, {
    params,
    responseType: "blob",
  });
  return response.data;
}

export async function getPassagensParityCheck(params = {}) {
  const res = await fetchData("/passagens/parity-check", "GET", null, { params });
  return res?.data ?? res;
}

/** Workspace — resumo de repasses XPay */
export async function getPassagensRepasseResumo(params = {}) {
  const res = await fetchData("/passagens/repasses/resumo", "GET", null, { params });
  return res?.data ?? res;
}

export async function getPassagensRepasseSolicitacoes(params = {}) {
  const res = await fetchData("/passagens/repasses/solicitacoes", "GET", null, { params });
  return res?.data ?? res;
}

export async function postPassagensRepasseSolicitacao(body = {}) {
  const res = await fetchData("/passagens/repasses/solicitacoes", "POST", body);
  return res?.data ?? res;
}

export async function cancelPassagensRepasseSolicitacao(id) {
  const res = await fetchData(`/passagens/repasses/solicitacoes/${id}/cancel`, "POST", {});
  return res?.data ?? res;
}

/** Console — fila de solicitações de repasse */
export async function getPlatformRepasseRequests(params = {}) {
  const { data } = await apiClient.get("/billing/admin/passenger-transport/repasse-requests", { params });
  return data?.data ?? null;
}

export async function patchPlatformRepasseRequest(id, body) {
  const { data } = await apiClient.patch(`/billing/admin/passenger-transport/repasse-requests/${id}`, body);
  return data?.data ?? null;
}

/** Portal do representante (viação) — KPIs multi-agência (sanitizado) */
export async function getPartnerPassengerSales(params = {}) {
  const { data } = await apiClient.get("/billing/partner/passenger-transport/sales", {
    params,
    timeout: 120000,
  });
  return data?.data ?? null;
}

/** Portal do representante (viação) — lista consolidada de vendas */
export async function getPartnerPassengerVendas(params = {}) {
  const { data } = await apiClient.get("/billing/partner/passenger-transport/vendas", {
    params,
    timeout: 120000,
  });
  return data?.data ?? null;
}

/** Portal do representante (viação) — detalhe de uma venda */
export async function getPartnerPassengerVendaDetail(dominioId, vendaId) {
  const d = encodeURIComponent(String(dominioId));
  const v = encodeURIComponent(String(vendaId));
  const { data } = await apiClient.get(`/billing/partner/passenger-transport/vendas/${d}/${v}`);
  return data?.data ?? null;
}

/** Portal do representante (viação) — export CSV sanitizado */
export async function exportPartnerPassengerVendasCsv(params = {}) {
  const response = await apiClient.get("/billing/partner/passenger-transport/export", {
    params,
    responseType: "blob",
    timeout: 120000,
  });
  return response.data;
}
